/**
 * Display formatters shared by the wizard's read-only surfaces — the summary
 * panel, the review, the calendar and the day-exception modal.
 *
 * Pure: the language and the translator are passed in, so this stays free of
 * React and i18next and can be tested without either.
 */

import type { Subagent } from '../types';
import { subagentLabel } from './subagents';
import { intlLocale } from '../../../utils/intlLocale';
import { dateTimeFormat, numberFormat, plainSpaces } from '../../../utils/intlFormatting';

/** The languages the app ships (`i18n.ts` → `supportedLngs`). */
export type AppLanguage = 'es' | 'en' | 'pt';

/** The app's language as a bare code; anything else reads as English, like i18n's fallback. */
export function appLanguageOf(lng: string | undefined): AppLanguage {
  const code = lng?.toLowerCase() ?? '';
  if (code.startsWith('es')) return 'es';
  if (code.startsWith('pt')) return 'pt';
  return 'en';
}

/** The subset of i18next's `t` these helpers need. */
export type Translate = (key: string, vars?: Record<string, unknown>) => string;

/*
 * Every figure and date of the feature goes through these, with the app's
 * language (`i18n.language`) read AT RENDER — never a formatter built at import
 * or a hardcoded locale, so switching the language re-renders them. The
 * language is mapped to a region (`intlLocale`: es → es-MX, pt → pt-BR), since a
 * bare 'es' groups the Spain way ("1234", "12.345").
 */

/** A number in the reader's language; `options` as `Intl.NumberFormat` takes them. */
export function formatNumber(
  n: number,
  lang: string | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  return plainSpaces(numberFormat(intlLocale(lang), options).format(n));
}

/** A count, rounded and grouped the way the reader's language groups it. */
export function formatCount(n: number, lang: string | undefined): string {
  return formatNumber(Math.round(n), lang, { maximumFractionDigits: 0 });
}

/**
 * A ratio (0–1) as a percentage, with the decimal mark of the reader's language:
 * "12.5%" / "12,5%" — the sign glued to the figure in the three languages, so
 * every percentage reads the same (`style: 'percent'` would put a space in some).
 * `Intl` rounds the decimal as written, half up: 23.45 → "23.5%".
 */
export function formatPercent(
  ratio: number,
  lang: string | undefined,
  digits: { min?: number; max?: number } = {},
): string {
  const { min = 1, max = 1 } = digits;
  const n = formatNumber(ratio * 100, lang, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
  return `${n}%`;
}

/**
 * An amount, "$1,234.50" / "$1.234,50": two decimals always, as money is read.
 * The sign stays "$" — the amounts carry no currency code to localise.
 */
export function formatMoney(n: number, lang: string | undefined): string {
  return `$${formatNumber(n, lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** A large number shortened the reader's way: "10.6M", "12.5 k", "10,6 mi". */
export function formatCompact(n: number, lang: string | undefined, fractionDigits = 1): string {
  return formatNumber(n, lang, {
    notation: 'compact',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** A date (Date or epoch ms) in the reader's language; '' when it is not one. */
export function formatDate(
  date: Date | number,
  lang: string | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  const epoch = typeof date === 'number' ? date : date.getTime();
  if (!Number.isFinite(epoch) || Number.isNaN(new Date(epoch).getTime())) return '';
  try {
    return plainSpaces(dateTimeFormat(intlLocale(lang), options).format(epoch));
  } catch {
    return '';
  }
}

/**
 * The dialling days as one label: "Mon · Wed · Fri", or a single word for the
 * two ends (none, every day). Sorted, so the order they were ticked in never
 * shows.
 */
export function weekdaysLabel(days: number[], t: Translate): string {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 0) return t('wizard.review.noDays');
  if (sorted.length === 7) return t('wizard.review.everyDay');
  return sorted.map((d) => t(`pacing.weekday.${d}`)).join(' · ');
}

/**
 * The dialling days as Silio says them: a run of three or more as a range
 * ("Mon–Fri"), anything else named one by one ("Mon, Wed"). Sorted and
 * de-duplicated first, so [5,1,2,3,4] still reads "Mon–Fri" — the order the
 * days were ticked in, or the order a sentence named them, never shows.
 *
 * Its copy lives under `assistant.schedule.*`: the review's `weekdaysLabel`
 * above is a different register ("Mon · Wed · Fri"), on purpose.
 */
export function daysLabel(days: number[], t: Translate): string {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (!sorted.length) return t('assistant.schedule.noDays');
  if (sorted.length === 7) return t('assistant.schedule.everyDay');
  const run = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  const name = (d: number) => t(`pacing.weekday.${d}`);
  if (run && sorted.length > 2) return `${name(sorted[0])}–${name(sorted[sorted.length - 1])}`;
  return sorted.map(name).join(', ');
}

/**
 * A "YYYY-MM-DD" date, read at local midnight and localised. Anything that is
 * not a date comes back as it was rather than as "Invalid Date".
 */
export function formatIsoDate(
  iso: string,
  lang: string | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? iso : formatDate(date, lang, options) || iso;
}

/** A subagent's name, or its translated positional fallback when unnamed. */
export function subagentName(sa: Subagent, index: number, t: Translate): string {
  return subagentLabel(sa, index, (n) => t('subagents.card.fallbackName', { n }));
}
